import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FolderPicker } from '../../folderPicker/FolderPicker';
import { SelectedItemTags } from '../../folderPicker/SelectedItemTags';
import { updateResourceParent } from '../../../apiServices/resourceApi';
import { serializeFuncs } from '../../../util/Sequentialize';
import { FinishedUploadsCounter } from '../fileUploadingModal/ProgressCounter';
import { useNotification } from '@/hooks/useNotification';
import { useAppDispatch } from '@/store/hooks';
import { invalidateParent } from '@/store/itemsSlice';
import { getButtonIsDisabled } from './MoveItemModalUtilityFunctions';
import { Button } from '../../ui/Button';
import { Spinner } from '../../ui/Spinner';

interface MoveItem {
  id: string;
  itemtype: string | { S: string };
  name?: string;
  title?: string;
  [k: string]: unknown;
}

interface MoveItemModalProps {
  selectedItems: MoveItem[];
  removeFromSelectedItems: (items: MoveItem[]) => void;
  refreshList: (force?: boolean, unused?: undefined, hard?: boolean) => void;
  closeModal: () => void;
  updateIsQuerying: (querying: boolean) => void;
  workspaceName?: string;
  match?: { params: { id: string } };
}

export default function MoveItemModal({
  selectedItems,
  removeFromSelectedItems,
  refreshList,
  closeModal,
  updateIsQuerying,
  workspaceName,
  match,
}: MoveItemModalProps) {
  const navigate = useNavigate();
  const [selectedDestination, setSelectedDestination] = useState<{ id: string } | 'root'>('root');
  const [moveStatus, setMoveStatus] = useState<string>('inactive');
  const [initialCount] = useState(selectedItems.length);
  const [uploading, setUploading] = useState('initial');
  const dispatch = useAppDispatch();
  const { notify } = useNotification();

  const updateSelectedDestination = (destination: { id: string } | undefined) => {
    if (destination) {
      setSelectedDestination(destination);
      setMoveStatus('active');
    } else {
      setSelectedDestination('root');
      setMoveStatus('inactive');
    }
  };

  const moveEntity = async (item: MoveItem) => {
    const destId = selectedDestination === 'root' ? 'root' : selectedDestination.id;
    const name = item.name || item.title || '';
    try {
      setMoveStatus('loading');
      updateIsQuerying(true);
      await updateResourceParent(item.id, name, destId);
      updateIsQuerying(false);
      setMoveStatus('inactive');
      removeFromSelectedItems([item]);
    } catch {
      setMoveStatus('error');
      updateIsQuerying(false);
      notify({ type: 'error', message: 'Could not move item' });
    }
  };

  const handleSubmit = async () => {
    if (selectedDestination !== 'root' && match?.params.id === selectedDestination?.id) {
      notify({ type: 'info', message: "Can't move files to current folder" });
      return;
    }
    setUploading('loading');
    if (selectedItems.length === 1) {
      await moveEntity(selectedItems[0]);
    } else {
      const funcs = selectedItems.map((item) => () => moveEntity(item));
      await serializeFuncs(funcs);
    }
    setUploading('success');
    // The destination's cached list doesn't have the moved items yet: fetch it again when it is opened
    dispatch(invalidateParent(selectedDestination === 'root' ? 'root' : selectedDestination.id));
    if (selectedDestination === 'root') {
      navigate('/in/folder/root');
    }
    refreshList(false, undefined, true);
    closeModal();
    notify({ type: 'success', message: selectedItems.length > 1 ? 'Items moved' : 'Item moved' });
  };

  return (
    <div className="space-y-3">
      <h4 className="text-sm font-medium text-muted-foreground">Moving</h4>
      <SelectedItemTags items={selectedItems} onRemove={removeFromSelectedItems} />
      <FolderPicker
        onSelectDestination={updateSelectedDestination}
        workspaceName={workspaceName}
        updateIsQuerying={updateIsQuerying}
        selectedItems={selectedItems}
        useCase="move"
        currentFolderId={match?.params?.id}
      />
      <Button
        onClick={handleSubmit}
        disabled={getButtonIsDisabled(selectedItems, selectedDestination) || moveStatus === 'loading'}
      >
        {moveStatus === 'loading' && <Spinner size="sm" />}
        {moveStatus === 'error' ? 'Try again' : 'Move'}
      </Button>
      {initialCount > 1 && (
        <FinishedUploadsCounter
          filesListLength={initialCount}
          fileUploadingProcessStatus={uploading}
          finishedUploadsListLength={initialCount - selectedItems.length}
          failedUploadsListLength={0}
        />
      )}
    </div>
  );
}
